import Validator from "../../helpers/validation";

const CustomSelect = {
    props: {
        title: {
            default: "",
            type: String,
        },

        name: {
            default: "",
            type: String,
        },

        required: {
            default: false,
            type: Boolean,
        },

        validation: {
            type: Array,
            default() {
                return [];
            },
        },

        initialValue: {
            default() {
                return {
                    text: null,
                    id: null,
                    icon: false,
                    selected: false,
                };
            },
            type: Object,
        },

        disabled: {
            default: false,
            type: Boolean,
        },

        options: {
            default() {
                return [];
            },
            type: Array,
        },

        selectId: {
            default: "",
            type: String,
        },
    },

    data() {
        return {
            option_icon: null,
            option_text: null,
            option_id: null,
            option_selected: false,

            error_message: null,

            is_open: false,

            valid: false,

            should_validate: false
        };
    },

    mounted() {
        if (this.initialValue.selected) {
            this.selectOption(this.initialValue, false);
        }

        this.setEventListeners();

        this.setDisabledClasses();
        this.setSelectClasses();
    },

    beforeDestroy() {
        document.removeEventListener("click", this.hideOptions);
    },

    watch: {
        validation() {
            this.validate();
            this.setValidationClasses();
        },

        initialValue: {
            handler() {
                this.selectOption(this.initialValue);
            },
            deep: true,
        },

        disabled() {
            this.setDisabledClasses();
        },

        option_icon() {
            this.setSelectClasses();
        },

        title() {
            this.setSelectClasses();
        },
    },

    methods: {
        selectOption(option, should_validate = true) {
            this.should_validate = should_validate;

            this.option_icon = option.icon;
            this.option_text = option.text;
            this.option_id = option.id;
            this.option_selected = true;

            const el = this.$refs["node"];

            if (this.option_text) {
                el.classList.add("has-value");
            } else {
                el.classList.remove("has-value");
            }

            this.hideOptions();
            this.validate();
            this.setValidationClasses();

            this.$emit("selected_option", {
                icon: this.option_icon,
                text: this.option_text,
                id: this.option_id,
                selected: this.option_selected
            });
        },

        clearOption() {
            this.option_icon = null;
            this.option_text = null;
            this.option_id = null;
            this.option_selected = false;

            this.hideOptions();
            this.validate();
            this.setValidationClasses();
        },

        toggleOptions() {
            this.is_open = !this.is_open;
        },

        hideOptions() {
            this.is_open = false;
        },

        validate() {
            if(!this.should_validate) {
                return;
            }

            if (!this.validation || !this.validation.length) {
                return;
            }

            const message = Validator.validate({
                validationArray: this.validation,
                value: this.option_text,
                name: this.name,
            });

            if (message) {
                this.error_message = message;
                this.valid = false;
                return;
            }

            this.error_message = null;
            this.valid = true;
        },

        hasValidationErrors() {
            this.should_validate = true;

            this.validate();
            this.setValidationClasses();

            if (!this.validation || !this.validation.length) {
                return false;
            }

            if (this.valid) {
                return false;
            }

            return true;
        },

        setValidationClasses() {
            if (this.loading) {
                return;
            }

            const el = this.$refs["node"];

            if (el) {
                if (this.error_message) {
                    el.classList.remove("valid");
                    el.classList.add("invalid");
                    return;
                }

                if (this.valid) {
                    el.classList.remove("invalid");
                    el.classList.add("valid");
                    return;
                }

                el.classList.remove("invalid");
                el.classList.remove("valid");
            }
        },

        setEventListeners() {
            const el = this.$refs["node"];

            const input = el.querySelector(
                "input[type=text], input[type=number], input[type=password], input[type=email]"
            );

            input.addEventListener("focus", () => {
                el.classList.add("focus");
            });

            input.addEventListener("blur", () => {
                el.classList.remove("focus");
            });

            document.addEventListener("click", (e) => {
                const target = e.target;
                if (this.selectId) {
                    const optionsElem = target.closest(`#${this.selectId}`);
                    if (!optionsElem) {
                        this.hideOptions();
                    }
                }
            });
        },

        setDisabledClasses() {
            if (this.loading) {
                return;
            }

            const el = this.$refs["node"];
 
            if (!el) {
                return;
            }

            if (this.disabled) {
                el.classList.add("disabled");
                return;
            }

            el.classList.remove("disabled");
        },

        setSelectClasses() {
            if (this.loading) {
                return;
            }

            const el = this.$refs["node"];

            if (!el) {
                return;
            }

            if (!this.title) {
                el.classList.add("not-label");
            } else {
                el.classList.remove("not-label");
            }

            if (this.option_icon) {
                el.classList.add("icon-prepend");
            } else {
                el.classList.remove("icon-prepend");
            }
        },
    },

    template: /*html*/ `
    <div class="field dropdown icon-append" :id="selectId" ref="node">
        <div v-if="option_icon" class="prepend-icon">
            <svg class="icon icon-statement-tab-card-icon">
                <use :xlink:href="this.$bitrix.Application.instance.php_vars.images_folder_path + option_icon"></use>
            </svg>
        </div>

        <label v-if="title">{{title}}<span v-if="required">*</span></label>

        <input type="text" readonly :disabled="disabled" v-model="option_text" @click.prevent="toggleOptions">

        <div class="append-icon">
            <svg class="icon icon-field-arrow">
                <use :xlink:href="this.$bitrix.Application.instance.php_vars.images_folder_path + '/icons/sprite.svg#field-arrow'"></use>
            </svg>
        </div>

        <div class="options" v-show="is_open && !disabled" >
            <div class="options__block">
                <ul>
                    <li v-for="option in options" :key="option.id">
                        <a @click.prevent="selectOption(option)" :class="{selected: option.selected}"> 
                            <svg v-if="option.icon" class="icon icon-statement-tab-card-icon">
                                <use :xlink:href="this.$bitrix.Application.instance.php_vars.images_folder_path + option.icon"></use>
                            </svg><span>{{option.text}}</span>
                        </a>
                    </li>
                </ul>
            </div>
        </div>

        <div class="input-errors" v-html="error_message"></div>
    </div>
  `,
};

export default CustomSelect;
